import HttpCotinService from './HttpCotinService';
import type { ComandoMenu } from '../modules/assistente-ia/modelos';
import { COMANDO_CRIAR_PROJETO, COMANDO_CRIAR_PROJETO_MENU } from '../modules/assistente-ia/projeto-spec';
import type { ProjetoSpec } from '../modules/assistente-ia/projeto-spec';

const CONTROLLER = 'BFFProxy/assistenteia';

export interface ChatResponse {
    resposta: string;
    sessaoId: number;
    titulo?: string | null;
    mensagemIdAssistente?: number;
}

/** Anexo no contrato do backend (AnexoChatDto): base64-in-JSON. */
export interface ChatAnexoRequest {
    tipo: string;
    mimeType: string;
    nomeArquivo: string;
    conteudoBase64: string;
}

export interface SessaoResumo {
    id: number;
    titulo: string;
    modeloId: string | null;
    dataAtualizacao: string;
}

export interface MensagemSessao {
    id?: number;
    papel: 'usuario' | 'assistente';
    conteudo: string;
    dataCriacao: string;
    feedback?: boolean | null;
}

export interface SessaoDetalhe {
    id: number;
    titulo: string;
    modeloId: string | null;
    mensagens: MensagemSessao[];
}

export interface PreferenciaUgarqui {
    modeloPreferido: string | null;
    tom: string | null;
    esforcoPreferido: string | null;
}

interface ChatResponseApiDto {
    resposta?: string;
    Resposta?: string;
    sessaoId?: number;
    SessaoId?: number;
    titulo?: string | null;
    Titulo?: string | null;
    mensagemIdAssistente?: number | null;
    MensagemIdAssistente?: number | null;
}

interface SessaoResumoApiDto {
    id?: number;
    Id?: number;
    titulo?: string;
    Titulo?: string;
    modeloId?: string | null;
    ModeloId?: string | null;
    dataAtualizacao?: string;
    DataAtualizacao?: string;
}

interface MensagemSessaoApiDto {
    id?: number;
    Id?: number;
    papel?: string;
    Papel?: string;
    conteudo?: string;
    Conteudo?: string;
    dataCriacao?: string;
    DataCriacao?: string;
    feedback?: boolean | null;
    Feedback?: boolean | null;
}

interface SessaoDetalheApiDto {
    id?: number;
    Id?: number;
    titulo?: string;
    Titulo?: string;
    modeloId?: string | null;
    ModeloId?: string | null;
    mensagens?: MensagemSessaoApiDto[];
    Mensagens?: MensagemSessaoApiDto[];
}

interface PreferenciaUgarquiApiDto {
    modeloPreferido?: string | null;
    ModeloPreferido?: string | null;
    tom?: string | null;
    Tom?: string | null;
    esforcoPreferido?: string | null;
    EsforcoPreferido?: string | null;
}

function mapSessaoResumo(dto: SessaoResumoApiDto): SessaoResumo {
    return {
        id: dto.id ?? dto.Id ?? 0,
        titulo: dto.titulo ?? dto.Titulo ?? '',
        modeloId: dto.modeloId ?? dto.ModeloId ?? null,
        dataAtualizacao: dto.dataAtualizacao ?? dto.DataAtualizacao ?? '',
    };
}

function mapMensagemSessao(dto: MensagemSessaoApiDto): MensagemSessao {
    const papel = dto.papel ?? dto.Papel ?? 'assistente';
    return {
        id: dto.id ?? dto.Id ?? undefined,
        papel: papel === 'usuario' ? 'usuario' : 'assistente',
        conteudo: dto.conteudo ?? dto.Conteudo ?? '',
        dataCriacao: dto.dataCriacao ?? dto.DataCriacao ?? '',
        feedback: dto.feedback ?? dto.Feedback ?? null,
    };
}

export async function sendChatMessage(
    mensagem: string,
    anexos?: ChatAnexoRequest[],
    modelo?: string,
    loginAd?: string,
    sessaoId?: number,
    signal?: AbortSignal
): Promise<ChatResponse> {
    const payload: {
        mensagem: string;
        anexos?: ChatAnexoRequest[];
        modelo?: string;
        loginAd?: string;
        sessaoId?: number;
    } = { mensagem };
    if (anexos && anexos.length > 0) {
        payload.anexos = anexos;
    }
    if (modelo) {
        payload.modelo = modelo;
    }
    if (loginAd) {
        payload.loginAd = loginAd;
    }
    if (sessaoId) {
        payload.sessaoId = sessaoId;
    }

    const data = await HttpCotinService.Post<ChatResponseApiDto>(`${CONTROLLER}/chat`, payload, signal);
    return {
        resposta: data?.resposta ?? data?.Resposta ?? '',
        sessaoId: data?.sessaoId ?? data?.SessaoId ?? 0,
        titulo: data?.titulo ?? data?.Titulo ?? null,
        mensagemIdAssistente: data?.mensagemIdAssistente ?? data?.MensagemIdAssistente ?? undefined,
    };
}

export interface ChatStreamFinal {
    sessaoId: number;
    titulo: string | null;
    mensagemIdAssistente: number | null;
}

/**
 * Consome o endpoint SSE de chat via fetch + ReadableStream (axios não dá acesso incremental ao
 * corpo da resposta no browser).
 *
 * Contrato do evento "chunk" na REDE: ou `{ delta }` (incremento a concatenar) ou `{ texto }`
 * (texto completo que substitui). O caminho comum é delta — antes o backend reemitia o prefixo
 * acumulado inteiro a cada flush, o que fazia uma resposta de N caracteres trafegar ~N²/2.
 *
 * Contrato do CALLBACK permanece o mesmo de antes: `onChunk` sempre recebe o texto COMPLETO a ser
 * exibido. A acumulação fica aqui, então nenhum componente precisou mudar.
 */
export async function sendChatMessageStreaming(
    mensagem: string,
    anexos: ChatAnexoRequest[] | undefined,
    modelo: string | undefined,
    esforcoRaciocinio: string | undefined,
    loginAd: string | undefined,
    sessaoId: number | undefined,
    onChunk: (texto: string) => void,
    signal: AbortSignal,
    comandosSelecionados?: string[],
    projetoSpec?: ProjetoSpec
): Promise<ChatStreamFinal> {
    const payload: Record<string, unknown> = { mensagem };
    if (anexos && anexos.length > 0) payload.anexos = anexos;
    if (modelo) payload.modelo = modelo;
    if (esforcoRaciocinio) payload.esforcoRaciocinio = esforcoRaciocinio;
    if (loginAd) payload.loginAd = loginAd;
    if (sessaoId) payload.sessaoId = sessaoId;
    if (comandosSelecionados && comandosSelecionados.length > 0) payload.comandosSelecionados = comandosSelecionados;
    if (projetoSpec) payload.projetoSpec = projetoSpec;

    let final: ChatStreamFinal | null = null;
    let textoExibido = '';

    try {
        const resposta = await fetch(`${HttpCotinService.getBaseUrl()}${CONTROLLER}/chat/stream`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(payload),
            signal,
        });

        if (!resposta.ok || !resposta.body) {
            throw new Error(`Falha ao iniciar o streaming (${resposta.status}).`);
        }

        const leitor = resposta.body.getReader();
        const decoder = new TextDecoder();
        let bufferBruto = '';

        while (true) {
            const { done, value } = await leitor.read();
            if (done) break;

            bufferBruto += decoder.decode(value, { stream: true });

            // Eventos SSE são separados por linha em branco ("\n\n"); processa cada bloco completo.
            let indiceFim: number;
            while ((indiceFim = bufferBruto.indexOf('\n\n')) !== -1) {
                const bloco = bufferBruto.slice(0, indiceFim);
                bufferBruto = bufferBruto.slice(indiceFim + 2);

                const linhaEvento = bloco.split('\n').find((l) => l.startsWith('event:'));
                const linhaDados = bloco.split('\n').find((l) => l.startsWith('data:'));
                if (!linhaEvento || !linhaDados) continue;

                const evento = linhaEvento.slice('event:'.length).trim();
                const dados = JSON.parse(linhaDados.slice('data:'.length).trim());

                if (evento === 'chunk') {
                    // `delta` concatena; `texto` substitui (desfecho do turno ou reescrita da
                    // redação de segredo, que um delta não conseguiria desfazer na tela).
                    textoExibido =
                        typeof dados.delta === 'string' ? textoExibido + dados.delta : (dados.texto as string);
                    onChunk(textoExibido);
                } else if (evento === 'done') {
                    final = dados as ChatStreamFinal;
                } else if (evento === 'erro') {
                    throw new Error((dados.mensagem as string) || 'Falha durante o streaming.');
                }
            }
        }
    } catch (error) {
        // `fetch`/`ReadableStreamDefaultReader.read()` abortados via AbortController rejeitam com
        // `DOMException { name: 'AbortError' }` — NÃO o `code: 'ERR_CANCELED'` que o axios usa
        // (HttpCotinService.tratarErro). Normaliza aqui pra manter um único contrato de
        // cancelamento em todo o service — o hook só precisa checar `code === 'ERR_CANCELED'`,
        // não importa se a chamada por baixo foi axios ou fetch.
        if (signal.aborted || (error as { name?: string })?.name === 'AbortError') {
            const erroCancelado = new Error('Streaming cancelado pelo usuário.');
            (erroCancelado as Error & { code: string }).code = 'ERR_CANCELED';
            throw erroCancelado;
        }
        throw error;
    }

    if (!final) {
        throw new Error('Stream encerrado sem confirmação final.');
    }
    return final;
}

export async function listarSessoes(loginAd: string, busca?: string): Promise<SessaoResumo[]> {
    const params: Record<string, unknown> = { loginAD: loginAd };
    if (busca) {
        params.busca = busca;
    }
    const data = await HttpCotinService.Get<SessaoResumoApiDto[]>(`${CONTROLLER}/sessoes`, params);
    return (data ?? []).map(mapSessaoResumo);
}

export async function obterMensagensDaSessao(sessaoId: number, loginAd: string): Promise<SessaoDetalhe> {
    const data = await HttpCotinService.Get<SessaoDetalheApiDto>(`${CONTROLLER}/sessoes/${sessaoId}/mensagens`, {
        loginAD: loginAd,
    });
    return {
        id: data?.id ?? data?.Id ?? sessaoId,
        titulo: data?.titulo ?? data?.Titulo ?? '',
        modeloId: data?.modeloId ?? data?.ModeloId ?? null,
        mensagens: (data?.mensagens ?? data?.Mensagens ?? []).map(mapMensagemSessao),
    };
}

export async function renomearSessao(sessaoId: number, loginAd: string, titulo: string): Promise<void> {
    await HttpCotinService.Put(`${CONTROLLER}/sessoes/${sessaoId}?loginAD=${encodeURIComponent(loginAd)}`, {
        titulo,
    });
}

export async function excluirSessao(sessaoId: number, loginAd: string): Promise<void> {
    await HttpCotinService.Delete(`${CONTROLLER}/sessoes/${sessaoId}`, { loginAD: loginAd });
}

export async function obterPreferencias(loginAd: string): Promise<PreferenciaUgarqui> {
    const data = await HttpCotinService.Get<PreferenciaUgarquiApiDto>(`${CONTROLLER}/preferencias`, {
        loginAD: loginAd,
    });
    return {
        modeloPreferido: data?.modeloPreferido ?? data?.ModeloPreferido ?? null,
        tom: data?.tom ?? data?.Tom ?? null,
        esforcoPreferido: data?.esforcoPreferido ?? data?.EsforcoPreferido ?? null,
    };
}

// Cache em módulo: a lista de comandos é estática por sessão de app (metadata de menu, não muda
// enquanto o usuário navega) — evita refazer o GET toda vez que o popover de comandos é aberto.
let comandosCache: ComandoMenu[] | null = null;

/** Limpa cache de comandos (útil após redeploy da API ou hot reload em dev). */
export function invalidarCacheComandos(): void {
    comandosCache = null;
}

function mesclarComandosLocais(comandos: ComandoMenu[]): ComandoMenu[] {
    if (comandos.some((c) => c.comando === COMANDO_CRIAR_PROJETO)) {
        return comandos;
    }
    return [...comandos, { ...COMANDO_CRIAR_PROJETO_MENU }];
}

/**
 * Lista os comandos slash (/comando) disponíveis para o menu, já sem a diretiva (server-only).
 * Mesmo padrão de `listarSessoes`/`obterPreferencias`: `HttpCotinService.Get` já desembrulha o
 * envelope `CustomResult` (`{ success, data, errors }`) e devolve só `data`. Falha na chamada não
 * pode travar o chat — o menu de comandos é "nice to have", então volta lista vazia em vez de
 * propagar o erro (o chamador não tem `.catch`).
 */
export async function getComandos(): Promise<ComandoMenu[]> {
    if (comandosCache) {
        comandosCache = mesclarComandosLocais(comandosCache);
        return comandosCache;
    }
    try {
        const data = await HttpCotinService.Get<ComandoMenu[]>(`${CONTROLLER}/comandos`);
        comandosCache = mesclarComandosLocais(data ?? []);
    } catch {
        return [];
    }
    return comandosCache;
}

/** Registra o voto (thumbs) do usuário numa mensagem do assistente. */
export async function registrarFeedback(mensagemId: number, loginAd: string, positivo: boolean): Promise<void> {
    await HttpCotinService.Post(
        `${CONTROLLER}/mensagens/${mensagemId}/feedback?loginAD=${encodeURIComponent(loginAd)}`,
        { positivo }
    );
}

export interface CorrigirMermaidResultado {
    sucesso: boolean;
    codigoCorrigido: string | null;
}

interface CorrigirMermaidApiDto {
    sucesso?: boolean;
    Sucesso?: boolean;
    codigoCorrigido?: string | null;
    CodigoCorrigido?: string | null;
}

/**
 * Tenta corrigir a sintaxe de um diagrama Mermaid que falhou ao renderizar no cliente — 1 chamada
 * de modelo barato, sem tools/histórico (ver `CorrigirMermaidService` no backend). Mesmo padrão de
 * `getComandos`: falha na chamada não pode travar o fallback existente (código cru), nunca propaga.
 */
export async function corrigirMermaid(codigo: string, erro: string): Promise<CorrigirMermaidResultado> {
    try {
        const data = await HttpCotinService.Post<CorrigirMermaidApiDto>(`${CONTROLLER}/corrigir-mermaid`, {
            codigo,
            erro,
        });
        return {
            sucesso: data?.sucesso ?? data?.Sucesso ?? false,
            codigoCorrigido: data?.codigoCorrigido ?? data?.CodigoCorrigido ?? null,
        };
    } catch {
        return { sucesso: false, codigoCorrigido: null };
    }
}
